import inRange from "lodash/inRange";
import isEmpty from "lodash/isEmpty";
import reduce from "lodash/reduce";
import uniq from "lodash/uniq";

import { UNKNOWN_BRAND_NAME } from "@stadium/constants/brands";

import {
  ITEM_PRODUCT_TYPES,
  PRICING_MATRIX_PRICE_ID,
  SHIPPED_SEPARATELY,
  SHIPPED_TOGETHER,
} from "@constants/orders/cart";
import {
  byo,
  OPTIONTYPE_COLOR_KEY,
  OPTIONTYPE_SIZE_KEY,
  SPREE_MEDLEY_ITEMS_SETS,
  SPREE_ORDER_PRODUCTS_SETS,
} from "@constants/products/detail";

import { convertPrice } from "@helpers/currency";
import { getVariantsExcludingMaster } from "@helpers/product-details";
import {
  isByoProduct,
  isCollectionTypeProduct,
  isSwagProduct,
  isThemedBoxProduct,
} from "@helpers/products/types";

const emptyArr = [];
const emptyObj = {};

export const getProductType = (intent) => {
  if (isCollectionTypeProduct(intent)) return ITEM_PRODUCT_TYPES["collection"];
  if (isByoProduct(intent)) return ITEM_PRODUCT_TYPES["byo"];
  return ITEM_PRODUCT_TYPES["product"];
};

export const formatRecipientOrder = (order) => {
  return {
    ...(Object.values(order.spreeRecipientOrders)[0] || {}),
    senderName: Object.values(order.spreeGiftDetails)[0]?.senderName,
  };
};

export const isOrderCompleted = (order) => {
  return order.state === "complete" || order.completedAt;
};

export const isOrderExpired = (order) => {
  return !!order.giftExpirationCompleted;
};

export const getCurrentQtyOfProductInCart = ({
  order,
  productId,
  currentCatalogExternalStoreId,
  editProductId = null,
  zoneId = "",
}) => {
  const item = order.lineItems?.find(
    (item) =>
      +item.productId === +productId &&
      +item?.storeId === +currentCatalogExternalStoreId &&
      editProductId !== item.id &&
      (!item?.zoneId || item.zoneId === +zoneId),
  );
  return item?.quantity || 0;
};

export const getRestrictionIdsString = (lineItem = {}) =>
  (lineItem?.orderProductSetRestrictions || emptyArr)
    .map((restriction) => restriction.id)
    .join(",");

export const updateLocalCartCurrency = (cartOrder, previewModeCurrencyCode, conversionRates) => {
  const lineItems = [...(cartOrder?.lineItems || emptyArr)];
  const medleyItemSets = [...(cartOrder?.medleyItemSets || emptyArr)];
  const fromCurrencyCode = [...lineItems, ...medleyItemSets]?.[0]?.localCartCurrency;
  if (!fromCurrencyCode) {
    return cartOrder;
  }
  const updatedLineItems = [];
  lineItems.forEach((item) => {
    const newItem = { ...item };
    const { price, localCartCurrency } = newItem;
    const priceInSelectedCurrency = convertPrice({
      from: localCartCurrency,
      to: previewModeCurrencyCode,
      value: price,
      conversionRates,
    });

    newItem.price = priceInSelectedCurrency;
    newItem.localCartCurrency = previewModeCurrencyCode;
    updatedLineItems.push(newItem);
  });
  const updatedMedleyItemSets = [];

  medleyItemSets.forEach((item) => {
    const newItem = { ...item };
    const { price, localCartCurrency, medleyComponents } = newItem;
    const priceInSelectedCurrency = convertPrice({
      from: localCartCurrency,
      to: previewModeCurrencyCode,
      value: price,
      conversionRates,
    });

    const updatedMedleyComponents = [];
    medleyComponents.forEach((item) => {
      const newItem = { ...item };
      const { price, localCartCurrency } = newItem;
      const priceInSelectedCurrency = convertPrice({
        from: localCartCurrency,
        to: previewModeCurrencyCode,
        value: price,
        conversionRates,
      });

      newItem.price = priceInSelectedCurrency;
      newItem.localCartCurrency = previewModeCurrencyCode;
      updatedMedleyComponents.push(newItem);
    });
    newItem.price = priceInSelectedCurrency;
    newItem.localCartCurrency = previewModeCurrencyCode;
    newItem.medleyComponents = updatedMedleyComponents;
    updatedMedleyItemSets.push(newItem);
  });

  const bagTotal = convertPrice({
    from: fromCurrencyCode,
    to: previewModeCurrencyCode,
    value: cartOrder.bagTotal || 0,
    conversionRates,
  });

  const cartTotals = {
    ...(cartOrder.cartTotals || {}),
    outstandingBalance: convertPrice({
      from: fromCurrencyCode,
      to: previewModeCurrencyCode,
      value: cartOrder.cartTotals?.outstandingBalance || 0,
      conversionRates,
    }),
    shopPointsContribution: convertPrice({
      from: fromCurrencyCode,
      to: previewModeCurrencyCode,
      value: cartOrder.cartTotals?.shopPointsContribution || 0,
      conversionRates,
    }),
  };

  return {
    ...cartOrder,
    bagTotal,
    cartTotals,
    checkoutTotals: cartTotals,
    lineItems: updatedLineItems,
    medleyItemSets: updatedMedleyItemSets,
  };
};

export const extractColorAndSizeFromVariant = (optionValues) =>
  (optionValues || []).reduce(
    (prev, current) => {
      if (current.optionType.key === OPTIONTYPE_COLOR_KEY) {
        prev.color = current;
      }
      if (OPTIONTYPE_SIZE_KEY.includes(current.optionType.key)) {
        prev.size = current;
      }
      return prev;
    },
    { color: {}, size: {} },
  );

export const getOptionRowsFromVariant = (row) => row?.previewModeOptionvalue || row?.optionValues;

export const getColorAndSizeFallbacks = ({ variant, variantSelected, hasExistingSizes }) => {
  const fromMappingRow = extractColorAndSizeFromVariant(getOptionRowsFromVariant(variant));
  const fromSelected = variantSelected
    ? extractColorAndSizeFromVariant(getOptionRowsFromVariant(variantSelected))
    : null;

  const color = fromMappingRow.color?.presentation || fromSelected?.color?.presentation;
  const sizeFallback = hasExistingSizes ? undefined : fromSelected?.size?.presentation;
  const size = fromMappingRow.size?.presentation || sizeFallback;

  return { color, size };
};

export const getCleanedAttributes = (attributes) =>
  Object.fromEntries(Object.entries(attributes).filter(([_, v]) => v != null && v !== ""));

export const getVendorName = (product) => {
  const { brand } = product || {};
  const { displayName } = brand || {};
  if (displayName) return displayName;
  return UNKNOWN_BRAND_NAME;
};

const createHashObject = (variant, item, hashObject, catalogVariant, isLockerShop = false) => {
  const { productId, quantity, price, storeId, id: itemId, lineItemCustomization } = item;
  const logoId = lineItemCustomization?.logoId;
  const {
    id: variantId,
    optionValues,
    product: { id, bulkAvailable, hasTierPricing },
    minOrderableQuantity,
  } = variant;

  const {
    size: { presentation: size },
    color: { presentation: color, name: colorHex },
  } = extractColorAndSizeFromVariant(optionValues);

  const isBulkItemOrLockerShop = bulkAvailable || isLockerShop || hasTierPricing;
  const uniqueId = isBulkItemOrLockerShop
    ? `${id}-${storeId}-${color}-${logoId}`
    : `${productId}-${storeId}`;

  if (uniqueId in hashObject) {
    hashObject[uniqueId].quantity += parseInt(quantity);
    if (isBulkItemOrLockerShop && size) {
      hashObject[uniqueId].sizeQtyMapping[size] = { qty: quantity, price };
    }
    hashObject[uniqueId].productIds.push(productId);
    hashObject[uniqueId].variantLineItemIds.push(itemId);
  } else {
    hashObject[uniqueId] = {
      ...item,
      ...(isBulkItemOrLockerShop && size
        ? { sizeQtyMapping: { [size]: { qty: quantity, price } } }
        : {}),
      ...(color ? { color, colorHex } : {}),
      productIds: [productId],
      variantLineItemIds: [itemId],
      catalogVariant,
      minOrderableQuantity,
      hasTierPricing,
      variantId,
    };
  }
};

export const generateLockerItems = (lineItems, variantsMapping) => {
  let hash = { designed: {}, customizable: {} };
  if (lineItems && variantsMapping) {
    lineItems.forEach((item) => {
      const { designed, customizable } = hash;
      const { productId, storeId } = item;
      const variant = variantsMapping[productId]?.[0];
      if (variant) {
        const { catalogVariants } = variant;
        const catalogVariant = catalogVariants?.find(
          ({ catalogStoreExternalId }) => String(catalogStoreExternalId) === String(storeId),
        );

        const hashObject = catalogVariant?.designed ? designed : customizable;
        const restaurantName = getVendorName(variant.product);
        createHashObject(variant, { ...item, restaurantName }, hashObject, catalogVariant, true);
      }
    });
  }
  return hash;
};

const mapLineItemsByRestaurantName = ({
  lineItems,
  restaurantName,
  isGuestOrPreviewMode,
  variantsMapping,
  hash,
}) => {
  lineItems.forEach((item) => {
    const { productId, storeId, lineItemCustomization } = item;
    const logoId = lineItemCustomization?.logoId;
    const variant = isGuestOrPreviewMode ? item : variantsMapping[productId]?.[0];
    if (variant?.product) {
      const { catalogVariants } = variant;

      const catalogVariant = catalogVariants?.find(
        ({ catalogStoreExternalId }) => String(catalogStoreExternalId) === String(storeId),
      );

      restaurantName = getVendorName(variant.product);

      hash[restaurantName] ??= {};

      const hashObject = hash[restaurantName];

      createHashObject(variant, { ...item, restaurantName }, hashObject, catalogVariant);
    } else {
      restaurantName = item.restaurantName || UNKNOWN_BRAND_NAME;
      if (restaurantName) {
        const {
          color: { presentation: color, name: colorHex },
        } = extractColorAndSizeFromVariant(item.optionValues);

        hash[restaurantName] = hash[restaurantName] || {};
        const uniqueIdKey = [productId, storeId, logoId].filter(Boolean).join("-");
        hash[restaurantName][uniqueIdKey] = { ...item, color, colorHex };
      }
    }
  });
};
export const generateShopsItems = (
  lineItems,
  variantsMapping,
  variantDataloading,
  isGuestOrPreviewMode = false,
) => {
  let hash = {};
  let restaurantName;
  if (lineItems && (isGuestOrPreviewMode || (variantsMapping && !variantDataloading))) {
    mapLineItemsByRestaurantName({
      lineItems,
      isGuestOrPreviewMode,
      variantsMapping,
      hash,
      restaurantName,
    });
    Object.keys(hash).forEach((name) => {
      hash[name] = Object.values(hash[name]);
    });
  }
  return hash;
};

export const computeExternalSourceIds = ({ lineItems, productSets = [], medleySets }) => {
  let lineItemIds = [
    ...(lineItems || emptyArr),
    ...(medleySets || emptyArr).map(({ medleyComponents }) => medleyComponents).flat(),
  ].map((item) => item.productId);
  lineItemIds = uniq(lineItemIds).join(",");
  let productSetIds = [
    ...(productSets || emptyArr).map((item) => item.pcbId),
    ...(medleySets || emptyArr).map((item) => item.medleyId),
  ].join(",");
  lineItemIds = lineItemIds ? `product&&${lineItemIds}` : "";
  productSetIds = productSetIds ? `product_set&&${productSetIds}` : "";
  const externalSourceIds = [lineItemIds, productSetIds].filter((id) => id).join("||");
  const catalogExternalStoreProductMappings = (lineItems || []).reduce((acc, item) => {
    if (item.storeId) acc[`${item.productId}_${item.storeId}`] = item.storeId;
    else acc[item.productId] = item.storeId;
    return acc;
  }, {});

  return {
    externalSourceIds,
    catalogExternalStoreProductMappings: JSON.stringify(catalogExternalStoreProductMappings),
  };
};

const getThemeBoxVariantImages = (item, productData) => {
  if (!isThemedBoxProduct(productData.intent)) return null;
  const images = productData.variantsIncludingMaster.find(
    (variant) => variant.priceIn === +item.price,
  )?.images;
  return images?.length ? images : null;
};

const getPresentImages = (images) => (images?.length ? images : null);

const getSwagVariantsFromCatalogStoreData = (item, productData) => {
  const { storeId = null } = item || emptyObj;
  const { allVariants = emptyArr, product = emptyObj, masterImages } = productData || emptyObj;
  const { intent = "" } = product;
  if (!isSwagProduct(intent))
    return { isSwagVariantSelectionAvailable: false, swagVariantFromCatalogStore: [] };
  const selectedVariantData = allVariants?.find(
    ({ catalogStoreExternalId }) => storeId == catalogStoreExternalId,
  );

  const catalogVariantImages = masterImages?.find(
    ({ catalogStoreExternalId }) => storeId == catalogStoreExternalId,
  );

  const { variants = emptyArr } = selectedVariantData || emptyObj;
  const variantsExcludingMaster = getVariantsExcludingMaster(variants);

  return {
    isSwagVariantSelectionAvailable: variantsExcludingMaster.length > 1,
    swagVariantFromCatalogStore: variants,
    catalogVariantImages,
  };
};

export const getUpdatedLineItemFromVariants = (item, variantsMapping, isSendToOthers = false) => {
  const data =
    variantsMapping[item.productId] ||
    variantsMapping[item.pcbId] ||
    variantsMapping[item.medleyId] ||
    [];
  const optionValues = data[0]?.optionValues || [];
  const name = data[0]?.product?.name || item.name;
  const slug = item?.catalogVariant?.productSlug || data[0]?.product?.slug || item.slug;

  const minOrderableQuantity = data[0]?.minOrderableQuantity;
  const masterVariant = data[0]?.product?.masterVariant || {};
  const isBulkAvailable = data[0]?.product?.bulkAvailable || false;
  const shippedSeparately = data[0]?.product?.shippedSeparately;
  const intent = data[0]?.product?.intent || "";
  const isMaster = data[0]?.isMaster;
  const productData = data[0]?.product || {};
  const swagmagicProductId = data[0]?.product?.id;
  const myDesignedProductImages = data[0]?.catalogVariants?.[0]?.images;
  const askForPreference = data[0]?.product?.askForPreference;
  const askForRestriction = data[0]?.product?.askForRestriction;
  const {
    isSwagVariantSelectionAvailable = false,
    swagVariantFromCatalogStore = emptyArr,
    catalogVariantImages = emptyObj,
  } = getSwagVariantsFromCatalogStoreData(item, data[0]);

  //Catalog variant images are only present if the product is added from the catalog, and if the storeId is present, it means the product is added from the catalog.
  let images =
    isSendToOthers && isSwagProduct(intent) && item?.storeId
      ? getPresentImages(catalogVariantImages?.images)
      : getThemeBoxVariantImages(item, data[0]?.product || {}) ||
        getPresentImages(item?.catalogVariant?.images) ||
        getPresentImages(data[0]?.images) ||
        /* Not adding an FF {variantListLimitQueryFieldsFlagEnabled} check here, since FF for this field will not be present in the response. */
        getPresentImages(data[0]?.masterImages?.[0]?.images) ||
        getPresentImages(data[0]?.product?.variantImages) ||
        [];

  return {
    ...item,
    images: images || emptyArr,
    optionValues,
    name,
    slug,
    minOrderableQuantity,
    masterVariant,
    isBulkAvailable,
    shippedSeparately,
    intent,
    isMaster,
    productData,
    swagmagicProductId,
    myDesignedProductImages,
    askForPreference,
    askForRestriction,
    isSwagVariantSelectionAvailable,
    swagVariantFromCatalogStore,
    catalogVariantImages,
  };
};

export const getLineItemsMapping = (lineItems) =>
  reduce(
    lineItems,
    (obj, item) => {
      obj[item.productId] = item;
      return obj;
    },
    {},
  );

export const getBundleProductsData = ({ lineItems, variantData }) =>
  lineItems.reduce((bundleCatalogMapping, item) => {
    const { catalogType, productType, product } = item;

    if (!bundleCatalogMapping[catalogType]) {
      bundleCatalogMapping[catalogType] = {
        total: 0,
      };
    }

    const currentCatalog = bundleCatalogMapping[catalogType];

    if (!currentCatalog[productType]) {
      currentCatalog[productType] = [];
    }

    const currItem = {
      total: variantData[product?.id]?.total,
      ...item,
    };

    currentCatalog[productType].push(currItem);

    currentCatalog.total += currItem?.quantity || 0;

    return bundleCatalogMapping;
  }, {});

export const getPricingMatrix = ({ variant }) => {
  if (variant && "prices" in variant) {
    const { prices } = variant;

    return prices.map(({ min, priceIn: price }, index) => {
      const nextPriceObj = prices[index + 1];
      const nextMinQuantity = Number(nextPriceObj?.min);
      const maxValidQuantity = Number.isNaN(nextMinQuantity) ? Infinity : nextMinQuantity;
      const minValidQuantity = index === 0 ? 0 : min;
      return {
        isInRange: (quantity) => inRange(quantity, minValidQuantity, maxValidQuantity),
        [PRICING_MATRIX_PRICE_ID]: price,
      };
    });
  }
  return null;
};

export const getLineItemImage = (variant, product) => {
  if (variant?.image) return variant.image;
  if (!isEmpty(variant?.images)) return variant.images[0]?.image || variant.images[0];
  if (!isEmpty(product?.variantImages)) return product?.variantImages[0];
};

export const getCartProductType = (type) => {
  switch (type) {
    case SPREE_MEDLEY_ITEMS_SETS:
      return byo;
    case SPREE_ORDER_PRODUCTS_SETS:
      return "collection";
    default:
      return "default";
  }
};

export const generateLineItemsMappedData = ({
  lineItems,
  variantsMapping,
  variantDataloading,
  isGuestOrPreviewMode = false,
}) => {
  let hashObject = {};
  if (
    !isEmpty(lineItems) &&
    (isGuestOrPreviewMode || (!isEmpty(variantsMapping) && !variantDataloading))
  ) {
    lineItems.forEach((item) => {
      const { productId, storeId, lineItemCustomization } = item;
      const logoId = lineItemCustomization?.logoId;
      const variant = isGuestOrPreviewMode ? item : variantsMapping[productId]?.[0];
      if (variant?.product) {
        const { catalogVariants } = variant;

        const catalogVariant = catalogVariants?.find(
          ({ catalogStoreExternalId }) => String(catalogStoreExternalId) === String(storeId),
        );
        createHashObject(variant, { ...item }, hashObject, catalogVariant);
      } else {
        const {
          color: { presentation: color, name: colorHex },
        } = extractColorAndSizeFromVariant(item?.optionValues || emptyArr);
        const uniqueIdKey = [productId, storeId, logoId].filter(Boolean).join("-");
        hashObject[uniqueIdKey] = { ...item, color, colorHex };
      }
    });
  }
  return Object.values(hashObject);
};

export const groupItemsByShippedSeparately = (list) => {
  const groupedProducts = list.reduce(
    (acc, item) => {
      item[SHIPPED_SEPARATELY]
        ? acc[SHIPPED_SEPARATELY].push(item)
        : acc[SHIPPED_TOGETHER].push(item);
      return acc;
    },
    { [SHIPPED_SEPARATELY]: [], [SHIPPED_TOGETHER]: [] },
  );
  const bothCategoriesPresent =
    groupedProducts[SHIPPED_SEPARATELY].length > 0 && groupedProducts[SHIPPED_TOGETHER].length > 0;

  return {
    bothCategoriesPresent,
    groupedProducts,
  };
};

export const getZoneListFromProductList = (productList) =>
  productList.reduce((zoneList, item) => [...zoneList, item.zoneId], []);

export const mapDietaryRestrictions = (restrictions) => {
  if (isEmpty(restrictions)) return undefined;
  return restrictions.map((r) => (typeof r === "object" ? r : { id: r }));
};
